/* eslint-disable no-redeclare */
import {
  AxisPaddingOptions,
  AxisRange,
  BaseChartResp,
  BubbleChartRespContent,
  isMarketRetailerType,
  RetailerType,
} from '@models/OpportunityReview';

function getAxisMinMax(values: number[], axisPaddingOptions?: AxisPaddingOptions): AxisRange {
  let max = Math.max(...values);
  let min = Math.min(...values);

  if (axisPaddingOptions) {
    const { axisMaxPaddingCalc, axisMinPaddingCalc } = axisPaddingOptions;
    max = axisMaxPaddingCalc(max);
    min = axisMinPaddingCalc(min);
  }

  return {
    max,
    min,
  };
}

export const getDataByRetailerType = (
  bubbleChartRespContent: BubbleChartRespContent[],
  retailerType: RetailerType,
): BaseChartResp[] => {
  if (bubbleChartRespContent.length === 0) {
    return [];
  }

  const { market, retailer } = bubbleChartRespContent[0];
  return isMarketRetailerType(retailerType) ? market : retailer;
};

const getAxisRange = (
  dataArray: BaseChartResp[],
  values: number[] | undefined,
  axisPaddingOptions: AxisPaddingOptions,
) => {
  let axisRange: AxisRange = {
    max: null,
    min: null,
  };

  // Only return the min and max range if there's more than one data
  const onlyOneData: boolean = dataArray?.every((data) => data.values?.length === 1);
  if (values && !onlyOneData) {
    axisRange = getAxisMinMax(values, axisPaddingOptions);
  }
  return axisRange;
};

const getYAxisRange = (dataArray: BaseChartResp[]) => {
  const yValues: number[] = dataArray?.flatMap((data) => data.values?.map((value) => value.ycoordinate));

  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const paddingMultiplier = 1.7;

  // Make sure the min is below the lowest value and max is above the highest value
  const paddedMinY = minY - Math.abs(minY) * paddingMultiplier; // Ensure it's below the min
  const paddedMaxY = maxY + Math.abs(maxY) * paddingMultiplier; // Ensure it's above the max

  return {
    yAxisRange: getAxisRange(dataArray, yValues, {
      axisMaxPaddingCalc: (max) => paddedMaxY, // Use the calculated padded max
      axisMinPaddingCalc: (min) => paddedMinY, // Use the calculated padded min
    }),
  };
};
const getXAxisRange = (dataArray: BaseChartResp[]) => {
  const xValues: number[] = dataArray
    ?.flatMap((data) => data.values?.map((value) => value.yourShare))
    .filter((value) => value);

  return {
    xAxisRange: getAxisRange(dataArray, xValues, {
      axisMaxPaddingCalc: (max) => max + 20,
      axisMinPaddingCalc: (min) => min - 20,
    }),
  };
};

export const getAxesRange = (dataArray: BaseChartResp[]) => {
  return {
    ...getXAxisRange(dataArray),
    ...getYAxisRange(dataArray),
  };
};
